/**
 * LoginModal - The Intelligent Standard
 *
 * Sophisticated authentication gate with cream background and Meridian brand styling.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useAuth } from '../contexts/AuthContext';
import { CheckMarkBullet, SignInFormContent } from './auth';

interface LoginModalProps {
  onClose?: () => void;
}

export function LoginModal({ onClose }: LoginModalProps) {
  const { signIn, signInWithEmail } = useAuth();

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleBackdropClick = () => {
    if (onClose) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 modal-backdrop-arena"
      style={{
        background: 'rgba(26, 26, 26, 0.6)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-[420px] rounded-xl modal-content-arena"
        style={{
          background: '#F5F0EB',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.16)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-8 pb-6">
          <div className="text-center mb-6">
            <h2
              className="font-serif text-3xl font-medium mb-3"
              style={{ color: '#1A1A1A', letterSpacing: '-0.01em' }}
            >
              Sign in to continue
            </h2>
            <p
              className="text-base leading-relaxed"
              style={{ color: '#6B6B6B' }}
            >
              Join the arena to vote on AI-generated spreadsheets and shape the
              future of financial modeling
            </p>
          </div>

          {/* Sign in card */}
          <div
            className="p-6 rounded-xl"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E8E0D8',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <SignInFormContent
              signIn={signIn}
              signInWithEmail={signInWithEmail}
              idPrefix="modal"
            />
          </div>
        </div>

        {/* Features list */}
        <div className="px-8 pb-8 space-y-3">
          <CheckMarkBullet text="Free access to leading AI models" />
          <CheckMarkBullet text="Compare their performance on practical tasks" />
          <CheckMarkBullet text="Shape the leaderboard" />
        </div>
      </div>
    </div>
  );

  // Render modal via portal to document.body
  return createPortal(modalContent, document.body);
}
